import { useAuth0 } from '@auth0/auth0-react';
import { CalendarDays, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE, AUTH_AUDIENCE } from '../../config/env';
import { useI18n } from '../../i18n/LanguageContext';

type Props = {
  providerUserId?: string | null;
  providerName?: string;
  providerTitle?: string | null;
  defaultLocationId?: string | null;
  locationOptions?: Array<{ id: string; name: string; isPrimary?: boolean }>;
};

type AppointmentTypeOption = { id: string; name: string; isTelemed?: boolean };
type PolicyState = {
  minimumAdvanceMinutes: number;
  maxDaysInFuture: number;
  allowSameDayBooking: boolean;
  allowPortalCancellation: boolean;
  cancellationNoticeHours: number;
  allowPortalReschedule: boolean;
  rescheduleNoticeHours: number;
  enforceWorkingHoursForBookableWindows: boolean;
  notes?: string | null;
};
type WorkingHoursRow = {
  weekday: number;
  recurrenceStartDate: string;
  recurrenceEndDate: string;
  recurrenceIntervalWeeks: number;
  startMinute: number;
  endMinute: number;
  active: boolean;
  allowBookableOutsideHours?: boolean;
  locationId?: string | null;
  notes?: string | null;
};
type BookableWindowRow = {
  weekday: number;
  recurrenceStartDate: string;
  recurrenceEndDate: string;
  recurrenceIntervalWeeks: number;
  appointmentTypeId: string;
  startMinute: number;
  endMinute: number;
  slotDurationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  maxBookingsPerSlot: number;
  visibleInPortal: boolean;
  active: boolean;
  allowOutsideWorkingHours?: boolean;
  existingPatientsOnly: boolean;
  newPatientsAllowed: boolean;
  locationId?: string | null;
  notes?: string | null;
};
type OverrideWindowRow = Omit<BookableWindowRow, 'weekday'>;
type OverrideRow = {
  date: string;
  kind: 'BLOCK_DAY' | 'BLOCK_RANGE' | 'ADD_AVAILABILITY' | 'REPLACE_DAY';
  startMinute?: number | null;
  endMinute?: number | null;
  reason?: string | null;
  active: boolean;
  notes?: string | null;
  windows: OverrideWindowRow[];
};
type SchedulingConfigState = { policy: PolicyState; workingHours: WorkingHoursRow[]; bookableWindows: BookableWindowRow[]; overrides: OverrideRow[] };
type RecurringDialogState<T extends { weekday: number }> = {
  open: boolean;
  index: number | null;
  value: T;
  selectedWeekdays: number[];
};
type DayPreview = {
  workingHours: Array<{ startMinute: number; endMinute: number; label: string }>;
  portalWindows: Array<{ startMinute: number; endMinute: number; label: string }>;
  blockedRanges: Array<{ startMinute: number; endMinute: number; label: string }>;
  appointments: Array<{ id: string; startMinute: number; endMinute: number; title: string; patientName: string; source: string; status: string }>;
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_PICKER_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAY_START = 6 * 60;
const DAY_END = 22 * 60;
const HOUR_HEIGHT = 42;

const DEFAULT_POLICY: PolicyState = {
  minimumAdvanceMinutes: 24 * 60,
  maxDaysInFuture: 60,
  allowSameDayBooking: false,
  allowPortalCancellation: true,
  cancellationNoticeHours: 24,
  allowPortalReschedule: true,
  rescheduleNoticeHours: 24,
  enforceWorkingHoursForBookableWindows: true,
  notes: '',
};
const DEFAULT_RECURRENCE_START = new Date().toISOString().slice(0, 10);
const DEFAULT_RECURRENCE_END = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
const DEFAULT_WORKING_ROW: WorkingHoursRow = { weekday: 1, recurrenceStartDate: DEFAULT_RECURRENCE_START, recurrenceEndDate: DEFAULT_RECURRENCE_END, recurrenceIntervalWeeks: 1, startMinute: 9 * 60, endMinute: 17 * 60, active: true, allowBookableOutsideHours: false, locationId: '', notes: '' };
const DEFAULT_BOOKABLE_WINDOW: BookableWindowRow = {
  weekday: 1,
  recurrenceStartDate: DEFAULT_RECURRENCE_START,
  recurrenceEndDate: DEFAULT_RECURRENCE_END,
  recurrenceIntervalWeeks: 1,
  appointmentTypeId: '',
  startMinute: 10 * 60,
  endMinute: 12 * 60,
  slotDurationMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  maxBookingsPerSlot: 1,
  visibleInPortal: true,
  active: true,
  allowOutsideWorkingHours: false,
  existingPatientsOnly: false,
  newPatientsAllowed: true,
  locationId: '',
  notes: '',
};
const DEFAULT_OVERRIDE_WINDOW: OverrideWindowRow = {
  recurrenceStartDate: DEFAULT_RECURRENCE_START,
  recurrenceEndDate: DEFAULT_RECURRENCE_END,
  recurrenceIntervalWeeks: 1,
  appointmentTypeId: '',
  startMinute: 10 * 60,
  endMinute: 12 * 60,
  slotDurationMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  maxBookingsPerSlot: 1,
  visibleInPortal: true,
  active: true,
  allowOutsideWorkingHours: false,
  existingPatientsOnly: false,
  newPatientsAllowed: true,
  locationId: '',
  notes: '',
};
const DEFAULT_OVERRIDE: OverrideRow = {
  date: new Date().toISOString().slice(0, 10),
  kind: 'BLOCK_RANGE',
  startMinute: 12 * 60,
  endMinute: 13 * 60,
  reason: '',
  active: true,
  notes: '',
  windows: [],
};

function weekdaySortKey(weekday: number) {
  return weekday === 0 ? 7 : weekday;
}
function orderedWeekdays(weekdays: number[]) {
  return Array.from(new Set(weekdays.filter((weekday) => Number.isInteger(weekday) && weekday >= 0 && weekday < 7))).sort((a, b) => weekdaySortKey(a) - weekdaySortKey(b));
}
function recurrenceIntervalLabel(interval: number, tr: (phrase: string) => string) {
  return interval === 1 ? tr('Every week') : `${tr('Every')} ${interval} ${tr('weeks')}`;
}
function weekdayPatternSummary(weekdays: number[], tr: (phrase: string) => string) {
  const normalized = orderedWeekdays(weekdays);
  if (!normalized.length) return tr('No weekdays selected');
  if (normalized.length === WEEKDAY_PICKER_ORDER.length) return tr('Every day');
  if (normalized.join(',') === '1,2,3,4,5') return tr('Weekdays');
  return normalized.map((weekday) => tr(WEEKDAYS[weekday])).join(', ');
}
function buildRecurringRows<T extends { weekday: number }>(value: T, selectedWeekdays: number[]) {
  return orderedWeekdays(selectedWeekdays).map((weekday) => ({ ...value, weekday }));
}
function replaceRecurringRowsAt<T>(rows: T[], index: number, nextRows: T[]) {
  const next = rows.filter((_, rowIndex) => rowIndex !== index);
  next.splice(Math.min(index, next.length), 0, ...nextRows);
  return next;
}

function minuteToInput(minute: number) {
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}
function inputToMinute(value: string) {
  const [hours, minutes] = value.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}
function rangeLabel(startMinute: number, endMinute: number) {
  return `${minuteToInput(startMinute)}-${minuteToInput(endMinute)}`;
}
function weekdayLabel(weekday: number, tr: (phrase: string) => string) {
  return WEEKDAYS[weekday] ? tr(WEEKDAYS[weekday]) : tr('Day');
}
function hourLabel(minute: number) {
  const hour = Math.floor(minute / 60);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:00 ${suffix}`;
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="rounded border-slate-300" />
      <span>{label}</span>
    </label>
  );
}

function SectionCard({ title, description, actions, children }: { title: string; description: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="card space-y-4 p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="font-semibold text-ink">{title}</h3>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Strip({ items, tone, emptyLabel }: { items: Array<{ startMinute: number; endMinute: number; label: string }>; tone: 'working' | 'portal' | 'blocked'; emptyLabel: string }) {
  const color = tone === 'working' ? 'bg-emerald-100 border-emerald-300 text-emerald-900' : tone === 'portal' ? 'bg-sky-100 border-sky-300 text-sky-900' : 'bg-amber-100 border-amber-300 text-amber-900';
  const total = DAY_END - DAY_START;
  return (
    <div className="space-y-2">
      <div className="relative h-14 overflow-hidden rounded-xl border border-slate-200 bg-[linear-gradient(90deg,rgba(148,163,184,0.10)_1px,transparent_1px)] bg-[size:12.5%_100%]">
        {!items.length ? <div className="flex h-full items-center px-3 text-sm text-slate-400">{emptyLabel}</div> : null}
        {items.map((item, index) => {
          const left = ((Math.max(item.startMinute, DAY_START) - DAY_START) / total) * 100;
          const width = Math.max(((Math.min(item.endMinute, DAY_END) - Math.max(item.startMinute, DAY_START)) / total) * 100, 3);
          return (
            <div key={`${item.label}-${index}`} className={`absolute top-2 bottom-2 overflow-hidden rounded-lg border px-2 py-1 text-[11px] font-medium ${color}`} style={{ left: `${left}%`, width: `${width}%` }}>
              <div className="truncate">{item.label}</div>
              <div className="truncate text-[10px] opacity-70">{rangeLabel(item.startMinute, item.endMinute)}</div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-5 text-[10px] uppercase tracking-[0.18em] text-slate-400">
        {[6, 10, 14, 18, 22].map((hour) => <span key={hour}>{hour}:00</span>)}
      </div>
    </div>
  );
}

function PreviewLane({ title, tone, items }: { title: string; tone: 'working' | 'portal' | 'blocked' | 'booked'; items: Array<{ startMinute: number; endMinute: number; label: string }> }) {
  const colors = { working: 'bg-emerald-100 border-emerald-300 text-emerald-900', portal: 'bg-sky-100 border-sky-300 text-sky-900', blocked: 'bg-amber-100 border-amber-300 text-amber-900', booked: 'bg-rose-100 border-rose-300 text-rose-900' } as const;
  const height = ((DAY_END - DAY_START) / 60) * HOUR_HEIGHT;
  return (
    <div className="min-w-[180px]">
      <div className="mb-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</div>
      <div className="relative rounded-xl border border-slate-200 bg-white" style={{ height }}>
        {Array.from({ length: (DAY_END - DAY_START) / 60 + 1 }, (_, index) => <div key={index} className="absolute left-0 right-0 border-t border-dashed border-slate-100" style={{ top: index * HOUR_HEIGHT }} />)}
        {items.map((item, index) => {
          const top = ((Math.max(item.startMinute, DAY_START) - DAY_START) / 60) * HOUR_HEIGHT;
          const blockHeight = Math.max(((Math.min(item.endMinute, DAY_END) - Math.max(item.startMinute, DAY_START)) / 60) * HOUR_HEIGHT, 24);
          return (
            <div key={`${title}-${index}`} className={`absolute left-2 right-2 overflow-hidden rounded-lg border px-2 py-1 text-[11px] shadow-sm ${colors[tone]}`} style={{ top, height: blockHeight }}>
              <div className="truncate font-semibold">{item.label}</div>
              <div className="truncate opacity-70">{rangeLabel(item.startMinute, item.endMinute)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlannerSection({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3">
        <h5 className="text-sm font-semibold text-ink">{title}</h5>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      {children}
    </section>
  );
}

function WeekdaySelector({ selectedWeekdays, onChange, tr }: { selectedWeekdays: number[]; onChange: (weekdays: number[]) => void; tr: (phrase: string) => string }) {
  const normalized = orderedWeekdays(selectedWeekdays);
  const toggleWeekday = (weekday: number) => {
    onChange(normalized.includes(weekday) ? normalized.filter((entry) => entry !== weekday) : orderedWeekdays([...normalized, weekday]));
  };
  const summary = weekdayPatternSummary(normalized, tr);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" onClick={() => onChange([1, 2, 3, 4, 5])}>{tr('Weekdays')}</button>
        <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" onClick={() => onChange(WEEKDAY_PICKER_ORDER)}>{tr('Every day')}</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {WEEKDAY_PICKER_ORDER.map((weekday) => {
          const checked = normalized.includes(weekday);
          return (
            <label key={weekday} title={tr(WEEKDAYS[weekday])} className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm transition ${checked ? 'border-ink bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}>
              <input type="checkbox" checked={checked} onChange={() => toggleWeekday(weekday)} className={`h-4 w-4 rounded border-slate-300 ${checked ? 'accent-white' : 'accent-ink'}`} />
              <span className="font-medium">{tr(WEEKDAY_SHORT[weekday])}</span>
            </label>
          );
        })}
      </div>
      <div className={`text-xs ${normalized.length ? 'text-slate-500' : 'text-rose-600'}`}>
        {normalized.length ? `${summary}. ${tr('Each checked day saves as its own recurring row.')}` : tr('Select at least one weekday.')}
      </div>
    </div>
  );
}

export default function ProviderSchedulingManager({ providerUserId, providerName, providerTitle, defaultLocationId, locationOptions = [] }: Props) {
  const { getAccessTokenSilently } = useAuth0();
  const { tr } = useI18n();
  const tm = tr;
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeOption[]>([]);
  const [config, setConfig] = useState<SchedulingConfigState>({ policy: DEFAULT_POLICY, workingHours: [], bookableWindows: [], overrides: [] });
  const [previewDate, setPreviewDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState<DayPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addAvailabilityDialogOpen, setAddAvailabilityDialogOpen] = useState(false);
  const [workingDialog, setWorkingDialog] = useState<RecurringDialogState<WorkingHoursRow>>({ open: false, index: null, value: { ...DEFAULT_WORKING_ROW }, selectedWeekdays: [DEFAULT_WORKING_ROW.weekday] });
  const [windowDialog, setWindowDialog] = useState<RecurringDialogState<BookableWindowRow>>({ open: false, index: null, value: { ...DEFAULT_BOOKABLE_WINDOW }, selectedWeekdays: [DEFAULT_BOOKABLE_WINDOW.weekday] });

  const getToken = useCallback(async () => getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } }), [getAccessTokenSilently]);
  const typeName = useCallback((typeId: string) => appointmentTypes.find((type) => type.id === typeId)?.name || tm('Visit type'), [appointmentTypes, tr]);
  const recurrenceLabel = useCallback((interval: number) => recurrenceIntervalLabel(interval, tr), [tr]);
  const weekdayLabelLocalized = useCallback((weekday: number) => weekdayLabel(weekday, tr), [tr]);
  const weekdayPatternSummaryLocalized = useCallback((weekdays: number[]) => weekdayPatternSummary(weekdays, tr), [tr]);
  const effectiveDefaultLocationId = defaultLocationId || locationOptions[0]?.id || '';
  const locationLabel = useCallback(
    (locationId?: string | null) => locationOptions.find((location) => location.id === locationId)?.name || tm('Unassigned'),
    [locationOptions, tm],
  );

  const loadOptions = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/appointment-types`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(tm('Failed to load appointment types.'));
    setAppointmentTypes(await res.json());
  }, [getToken, tr]);

  const loadConfig = useCallback(async () => {
    if (!providerUserId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/scheduling/providers/${providerUserId}/config`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(tm('Failed to load scheduling.'));
      const data = await res.json();
      setConfig({ policy: { ...DEFAULT_POLICY, ...(data.policy || {}) }, workingHours: Array.isArray(data.workingHours) ? data.workingHours : [], bookableWindows: Array.isArray(data.bookableWindows) ? data.bookableWindows : [], overrides: Array.isArray(data.overrides) ? data.overrides : [] });
    } catch (err: any) {
      setError(err.message || tm('Failed to load scheduling configuration.'));
    } finally {
      setLoading(false);
    }
  }, [getToken, providerUserId, tr]);

  const loadPreview = useCallback(async () => {
    if (!providerUserId || !previewDate) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/scheduling/providers/${providerUserId}/day-preview?date=${encodeURIComponent(previewDate)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(tm('Failed to load day preview.'));
      setPreview(await res.json());
    } catch (err: any) {
      setError(err.message || tm('Failed to load day preview.'));
    }
  }, [getToken, previewDate, providerUserId, tr]);

  useEffect(() => { void loadOptions(); }, [loadOptions]);
  useEffect(() => {
    setConfig({ policy: DEFAULT_POLICY, workingHours: [], bookableWindows: [], overrides: [] });
    setPreview(null);
    if (providerUserId) void loadConfig();
  }, [loadConfig, providerUserId]);
  useEffect(() => { if (providerUserId) void loadPreview(); }, [loadPreview, providerUserId]);

  const workingRows = useMemo(
    () => config.workingHours.map((row, index) => ({ row, index })).sort((a, b) => a.row.recurrenceStartDate.localeCompare(b.row.recurrenceStartDate) || weekdaySortKey(a.row.weekday) - weekdaySortKey(b.row.weekday) || a.row.startMinute - b.row.startMinute),
    [config.workingHours],
  );
  const bookableRows = useMemo(
    () => config.bookableWindows.map((row, index) => ({ row, index })).sort((a, b) => a.row.recurrenceStartDate.localeCompare(b.row.recurrenceStartDate) || weekdaySortKey(a.row.weekday) - weekdaySortKey(b.row.weekday) || a.row.startMinute - b.row.startMinute),
    [config.bookableWindows],
  );
  const recurringRows = useMemo(
    () => [
      ...workingRows.map(({ row, index }) => ({ kind: 'WORKING' as const, index, row })),
      ...bookableRows.map(({ row, index }) => ({ kind: 'PORTAL' as const, index, row })),
    ].sort((a, b) => a.row.recurrenceStartDate.localeCompare(b.row.recurrenceStartDate) || weekdaySortKey(a.row.weekday) - weekdaySortKey(b.row.weekday) || a.row.startMinute - b.row.startMinute),
    [bookableRows, workingRows],
  );

  async function saveConfig() {
    if (!providerUserId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const normalizeLocationId = (value?: string | null) => value?.trim() || effectiveDefaultLocationId || null;
      const payload = {
        ...config,
        workingHours: config.workingHours.map((row) => ({ ...row, locationId: normalizeLocationId(row.locationId) })),
        bookableWindows: config.bookableWindows.map((row) => ({ ...row, locationId: normalizeLocationId(row.locationId) })),
        overrides: config.overrides.map((override) => ({
          ...override,
          windows: override.windows.map((window) => ({ ...window, locationId: normalizeLocationId(window.locationId) })),
        })),
      };
      const token = await getToken();
      const res = await fetch(`${API_BASE}/scheduling/providers/${providerUserId}/config`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.message || tm('Failed to save scheduling.'));
      }
      setSuccess(tm('Provider scheduling saved.'));
      await loadConfig();
      await loadPreview();
      window.setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || tm('Failed to save scheduling configuration.'));
    } finally {
      setSaving(false);
    }
  }

  const removeWorking = (index: number) => setConfig((current) => ({ ...current, workingHours: current.workingHours.filter((_, entryIndex) => entryIndex !== index) }));
  const removeWindow = (index: number) => setConfig((current) => ({ ...current, bookableWindows: current.bookableWindows.filter((_, entryIndex) => entryIndex !== index) }));
  const updateOverride = (index: number, patch: Partial<OverrideRow>) => setConfig((current) => ({ ...current, overrides: current.overrides.map((entry, entryIndex) => entryIndex === index ? { ...entry, ...patch } : entry) }));
  const removeOverride = (index: number) => setConfig((current) => ({ ...current, overrides: current.overrides.filter((_, entryIndex) => entryIndex !== index) }));
  const addOverride = (kind: OverrideRow['kind']) => setConfig((current) => ({ ...current, overrides: [...current.overrides, { ...DEFAULT_OVERRIDE, kind, windows: kind === 'ADD_AVAILABILITY' || kind === 'REPLACE_DAY' ? [{ ...DEFAULT_OVERRIDE_WINDOW, appointmentTypeId: appointmentTypes[0]?.id || '', locationId: effectiveDefaultLocationId }] : [] }] }));
  const updateOverrideWindow = (overrideIndex: number, windowIndex: number, patch: Partial<OverrideWindowRow>) => setConfig((current) => ({ ...current, overrides: current.overrides.map((entry, entryIndex) => entryIndex === overrideIndex ? { ...entry, windows: entry.windows.map((windowEntry, nestedIndex) => nestedIndex === windowIndex ? { ...windowEntry, ...patch } : windowEntry) } : entry) }));
  const addOverrideWindow = (overrideIndex: number) => setConfig((current) => ({ ...current, overrides: current.overrides.map((entry, entryIndex) => entryIndex === overrideIndex ? { ...entry, windows: [...entry.windows, { ...DEFAULT_OVERRIDE_WINDOW, appointmentTypeId: appointmentTypes[0]?.id || '', locationId: effectiveDefaultLocationId }] } : entry) }));
  const removeOverrideWindow = (overrideIndex: number, windowIndex: number) => setConfig((current) => ({ ...current, overrides: current.overrides.map((entry, entryIndex) => entryIndex === overrideIndex ? { ...entry, windows: entry.windows.filter((_, nestedIndex) => nestedIndex !== windowIndex) } : entry) }));

  function closeWorkingDialog() {
    setWorkingDialog({ open: false, index: null, value: { ...DEFAULT_WORKING_ROW, locationId: effectiveDefaultLocationId }, selectedWeekdays: [DEFAULT_WORKING_ROW.weekday] });
  }
  function closeWindowDialog() {
    setWindowDialog({ open: false, index: null, value: { ...DEFAULT_BOOKABLE_WINDOW, appointmentTypeId: appointmentTypes[0]?.id || '', locationId: effectiveDefaultLocationId }, selectedWeekdays: [DEFAULT_BOOKABLE_WINDOW.weekday] });
  }
  function updateWorkingSelectedWeekdays(selectedWeekdays: number[]) {
    const normalized = orderedWeekdays(selectedWeekdays);
    setWorkingDialog((current) => ({ ...current, selectedWeekdays: normalized, value: normalized.length ? { ...current.value, weekday: normalized[0] } : current.value }));
  }
  function updateWindowSelectedWeekdays(selectedWeekdays: number[]) {
    const normalized = orderedWeekdays(selectedWeekdays);
    setWindowDialog((current) => ({ ...current, selectedWeekdays: normalized, value: normalized.length ? { ...current.value, weekday: normalized[0] } : current.value }));
  }

  function openWorkingDialog(index?: number) {
    const row = index !== undefined
      ? { ...config.workingHours[index], locationId: config.workingHours[index]?.locationId || effectiveDefaultLocationId }
      : { ...DEFAULT_WORKING_ROW, locationId: effectiveDefaultLocationId };
    setWorkingDialog({ open: true, index: index ?? null, value: { ...row }, selectedWeekdays: [row.weekday] });
  }

  function saveWorkingDialog() {
    const nextRows = buildRecurringRows(workingDialog.value, workingDialog.selectedWeekdays);
    if (!nextRows.length) return;
    setConfig((current) => {
      if (workingDialog.index === null) {
        return { ...current, workingHours: [...current.workingHours, ...nextRows] };
      }
      return {
        ...current,
        workingHours: replaceRecurringRowsAt(current.workingHours, workingDialog.index, nextRows),
      };
    });
    closeWorkingDialog();
  }

  function openWindowDialog(index?: number) {
    const row = index !== undefined
      ? { ...config.bookableWindows[index], locationId: config.bookableWindows[index]?.locationId || effectiveDefaultLocationId }
      : { ...DEFAULT_BOOKABLE_WINDOW, appointmentTypeId: appointmentTypes[0]?.id || '', locationId: effectiveDefaultLocationId };
    setWindowDialog({ open: true, index: index ?? null, value: { ...row }, selectedWeekdays: [row.weekday] });
  }

  function saveWindowDialog() {
    const nextRows = buildRecurringRows(windowDialog.value, windowDialog.selectedWeekdays);
    if (!nextRows.length) return;
    setConfig((current) => {
      if (windowDialog.index === null) {
        return { ...current, bookableWindows: [...current.bookableWindows, ...nextRows] };
      }
      return {
        ...current,
        bookableWindows: replaceRecurringRowsAt(current.bookableWindows, windowDialog.index, nextRows),
      };
    });
    closeWindowDialog();
  }

  return (
    <div className="space-y-6">
      <div className="card p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-ink">{tm('Provider Self-Scheduling')}</h2>
            <p className="text-sm text-slate-500">{tm('Build the provider-facing schedule first, then expose only generated patient-bookable slots. The raw agenda stays hidden from the portal.')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => void loadConfig()}><RefreshCw size={14} /> {tm('Reload')}</button>
            <button className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" onClick={() => void saveConfig()} disabled={!providerUserId || saving}><Save size={14} /> {saving ? tm('Saving...') : tm('Save schedule')}</button>
          </div>
        </div>
        {(providerName || providerTitle) ? <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600">{tm('Scheduling for')} <span className="font-semibold text-slate-800">{providerName || tm('Provider')}</span>{providerTitle ? ` · ${providerTitle}` : ''}</div> : null}
        {loading ? <div className="mt-4 text-sm text-slate-500">{tm('Loading provider schedule...')}</div> : null}
        {error ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
        {success ? <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div> : null}
      </div>

      <SectionCard title={tm('Booking Policy')} description={tm('These rules apply to portal slot generation and patient self-service actions.')}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Minimum advance notice')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={config.policy.minimumAdvanceMinutes} onChange={(event) => setConfig((current) => ({ ...current, policy: { ...current.policy, minimumAdvanceMinutes: Number(event.target.value) || 0 } }))} /></label>
          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Max days in future')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={config.policy.maxDaysInFuture} onChange={(event) => setConfig((current) => ({ ...current, policy: { ...current.policy, maxDaysInFuture: Number(event.target.value) || 1 } }))} /></label>
          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Cancel notice hours')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={config.policy.cancellationNoticeHours} onChange={(event) => setConfig((current) => ({ ...current, policy: { ...current.policy, cancellationNoticeHours: Number(event.target.value) || 0 } }))} /></label>
          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Reschedule notice hours')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={config.policy.rescheduleNoticeHours} onChange={(event) => setConfig((current) => ({ ...current, policy: { ...current.policy, rescheduleNoticeHours: Number(event.target.value) || 0 } }))} /></label>
        </div>
        <div className="flex flex-wrap gap-4">
          <Toggle label={tm('Allow same-day booking')} checked={config.policy.allowSameDayBooking} onChange={(checked) => setConfig((current) => ({ ...current, policy: { ...current.policy, allowSameDayBooking: checked } }))} />
          <Toggle label={tm('Allow portal cancellation')} checked={config.policy.allowPortalCancellation} onChange={(checked) => setConfig((current) => ({ ...current, policy: { ...current.policy, allowPortalCancellation: checked } }))} />
          <Toggle label={tm('Allow portal reschedule')} checked={config.policy.allowPortalReschedule} onChange={(checked) => setConfig((current) => ({ ...current, policy: { ...current.policy, allowPortalReschedule: checked } }))} />
          <Toggle label={tm('Enforce working hours for bookable windows')} checked={config.policy.enforceWorkingHoursForBookableWindows} onChange={(checked) => setConfig((current) => ({ ...current, policy: { ...current.policy, enforceWorkingHoursForBookableWindows: checked } }))} />
        </div>
      </SectionCard>

      <SectionCard title={tm('Recurring Availability')} description={tm('Manage recurring availability in one table. Each row carries its weekday, date range, and recurrence interval, similar to Outlook-style recurrence patterns.')}>
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div>
                <h4 className="font-semibold text-ink">{tm('All Recurring Availability')}</h4>
                <p className="text-sm text-slate-500">{tm('Working-hours rows and portal-bookable rows are managed together here.')}</p>
              </div>
              <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setAddAvailabilityDialogOpen(true)}>
                <Plus size={14} /> {tm('Add new')}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">{tm('Type')}</th>
                    <th className="px-4 py-3 font-medium">{tm('Weekday')}</th>
                    <th className="px-4 py-3 font-medium">{tm('Start date')}</th>
                    <th className="px-4 py-3 font-medium">{tm('End date')}</th>
                    <th className="px-4 py-3 font-medium">{tm('Repeats')}</th>
                    <th className="px-4 py-3 font-medium">{tm('Time')}</th>
                    <th className="px-4 py-3 font-medium">{tm('Location')}</th>
                    <th className="px-4 py-3 font-medium">{tm('Details')}</th>
                    <th className="px-4 py-3 font-medium">{tm('Status')}</th>
                    <th className="px-4 py-3 font-medium text-right">{tm('Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recurringRows.length ? recurringRows.map(({ kind, row, index }) => (
                    <tr key={`recurring-row-${kind}-${index}`}>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${kind === 'WORKING' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'}`}>
                          {kind === 'WORKING' ? tm('Working') : tm('Portal')}
                        </span>
                      </td>
                      <td className="px-4 py-3">{weekdayLabelLocalized(row.weekday)}</td>
                      <td className="px-4 py-3">{row.recurrenceStartDate}</td>
                      <td className="px-4 py-3">{row.recurrenceEndDate}</td>
                      <td className="px-4 py-3">{recurrenceLabel(row.recurrenceIntervalWeeks)}</td>
                      <td className="px-4 py-3">{rangeLabel(row.startMinute, row.endMinute)}</td>
                      <td className="px-4 py-3 text-slate-600">{locationLabel((row as WorkingHoursRow).locationId)}</td>
                      <td className="px-4 py-3">
                        {kind === 'WORKING'
                          ? tm('Staff-facing working hours')
                          : `${typeName((row as BookableWindowRow).appointmentTypeId)} · ${(row as BookableWindowRow).slotDurationMinutes} ${tm('min')} ${tm('slots')}`}
                      </td>
                      <td className="px-4 py-3">
                        {kind === 'WORKING'
                          ? row.active ? tm('Active') : tm('Inactive')
                          : (row as BookableWindowRow).visibleInPortal ? tm('Portal visible') : tm('Hidden')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50" onClick={() => kind === 'WORKING' ? openWorkingDialog(index) : openWindowDialog(index)}>{tm('Edit')}</button>
                          <button className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50" onClick={() => kind === 'WORKING' ? removeWorking(index) : removeWindow(index)}>{tm('Delete')}</button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={10} className="px-4 py-6 text-center text-slate-500">{tm('No recurring availability yet.')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={tm('Date Overrides')} description={tm('Use date-level actions to block time, add extra availability, or replace a day without exposing internal calendar detail.')} actions={<div className="flex flex-wrap gap-2"><button className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => addOverride('BLOCK_DAY')}>{tm('Block day')}</button><button className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => addOverride('BLOCK_RANGE')}>{tm('Block time')}</button><button className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => addOverride('ADD_AVAILABILITY')}>{tm('Add availability')}</button><button className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => addOverride('REPLACE_DAY')}>{tm('Replace day')}</button></div>}>
        <div className="space-y-4">
          {config.overrides.length ? config.overrides.map((override, index) => {
            const stripItems = override.kind === 'BLOCK_DAY'
              ? [{ startMinute: DAY_START, endMinute: DAY_END, label: override.reason || tm('Blocked all day') }]
              : override.kind === 'BLOCK_RANGE'
                ? [{ startMinute: override.startMinute || DAY_START, endMinute: override.endMinute || DAY_END, label: override.reason || tm('Blocked') }]
                : override.windows.map((window) => ({ startMinute: window.startMinute, endMinute: window.endMinute, label: typeName(window.appointmentTypeId) }));
            const nextKindChange = (kind: OverrideRow['kind']) => updateOverride(index, { kind, windows: kind === 'ADD_AVAILABILITY' || kind === 'REPLACE_DAY' ? override.windows.length ? override.windows : [{ ...DEFAULT_OVERRIDE_WINDOW, appointmentTypeId: appointmentTypes[0]?.id || '' }] : [] });
            return (
              <div key={`override-${index}`} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-[180px_220px_minmax(0,1fr)]">
                    <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Date')}</span><input type="date" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={override.date} onChange={(event) => updateOverride(index, { date: event.target.value })} /></label>
                    <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Action')}</span><select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={override.kind} onChange={(event) => nextKindChange(event.target.value as OverrideRow['kind'])}><option value="BLOCK_DAY">{tm('Block full day')}</option><option value="BLOCK_RANGE">{tm('Block range')}</option><option value="ADD_AVAILABILITY">{tm('Add availability')}</option><option value="REPLACE_DAY">{tm('Replace day')}</option></select></label>
                    <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Reason')}</span><input className="w-full rounded-lg border border-slate-200 px-3 py-2" value={override.reason || ''} onChange={(event) => updateOverride(index, { reason: event.target.value })} placeholder={tm('Vacation, lunch, emergency block, meeting...')} /></label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Toggle label={tm('Active')} checked={override.active} onChange={(checked) => updateOverride(index, { active: checked })} />
                    <button className="inline-flex items-center justify-center rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700" onClick={() => removeOverride(index)}><Trash2 size={14} /></button>
                  </div>
                </div>

                <Strip items={stripItems} tone={override.kind === 'BLOCK_DAY' || override.kind === 'BLOCK_RANGE' ? 'blocked' : 'portal'} emptyLabel={tm('No override windows configured')} />

                {override.kind === 'BLOCK_RANGE' ? <div className="grid grid-cols-1 gap-3 md:grid-cols-2"><label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Start')}</span><input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={minuteToInput(override.startMinute || 0)} onChange={(event) => updateOverride(index, { startMinute: inputToMinute(event.target.value) })} /></label><label className="text-sm"><span className="mb-1 block text-slate-500">{tm('End')}</span><input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={minuteToInput(override.endMinute || 0)} onChange={(event) => updateOverride(index, { endMinute: inputToMinute(event.target.value) })} /></label></div> : null}

                {override.kind === 'ADD_AVAILABILITY' || override.kind === 'REPLACE_DAY' ? <div className="space-y-3">
                  {override.windows.map((window, windowIndex) => (
                    <div key={`override-window-${index}-${windowIndex}`} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.4fr)_1fr_1fr_120px_auto]">
                        <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Visit type')}</span><select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={window.appointmentTypeId} onChange={(event) => updateOverrideWindow(index, windowIndex, { appointmentTypeId: event.target.value })}><option value="">{tm('Visit type')}</option>{appointmentTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>
                        <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Start')}</span><input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={minuteToInput(window.startMinute)} onChange={(event) => updateOverrideWindow(index, windowIndex, { startMinute: inputToMinute(event.target.value) })} /></label>
                        <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('End')}</span><input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={minuteToInput(window.endMinute)} onChange={(event) => updateOverrideWindow(index, windowIndex, { endMinute: inputToMinute(event.target.value) })} /></label>
                        <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Slot')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={window.slotDurationMinutes} onChange={(event) => updateOverrideWindow(index, windowIndex, { slotDurationMinutes: Number(event.target.value) || 30 })} /></label>
                        <button className="inline-flex items-center justify-center rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700" onClick={() => removeOverrideWindow(index, windowIndex)}><Trash2 size={14} /></button>
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                        <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Buffer before')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={window.bufferBeforeMinutes} onChange={(event) => updateOverrideWindow(index, windowIndex, { bufferBeforeMinutes: Number(event.target.value) || 0 })} /></label>
                        <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Buffer after')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={window.bufferAfterMinutes} onChange={(event) => updateOverrideWindow(index, windowIndex, { bufferAfterMinutes: Number(event.target.value) || 0 })} /></label>
                        <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Max bookings')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={window.maxBookingsPerSlot} onChange={(event) => updateOverrideWindow(index, windowIndex, { maxBookingsPerSlot: Number(event.target.value) || 1 })} /></label>
                        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white px-3 py-2"><Toggle label={tm('Portal visible')} checked={window.visibleInPortal} onChange={(checked) => updateOverrideWindow(index, windowIndex, { visibleInPortal: checked })} /><Toggle label={tm('Active')} checked={window.active} onChange={(checked) => updateOverrideWindow(index, windowIndex, { active: checked })} /></div>
                      </div>
                    </div>
                  ))}
                  <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => addOverrideWindow(index)}><Plus size={14} /> {tm('Add override window')}</button>
                </div> : null}
              </div>
            );
          }) : <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">{tm('No date overrides yet. Use the quick actions above to block time or open extra availability.')}</div>}
        </div>
      </SectionCard>

      {addAvailabilityDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-[32px] bg-slate-50 p-2 shadow-2xl">
            <div className="rounded-[28px] bg-white p-6">
              <div className="flex flex-col gap-3 border-b border-slate-100 pb-5 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Recurring Availability')}</p>
                  <h4 className="text-2xl font-semibold text-ink">{tm('Add new availability')}</h4>
                  <p className="mt-1 text-sm text-slate-500">{tm('Pick the series you want to build, then configure the weekly recurrence in the planner.')}</p>
                </div>
                <button className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={() => setAddAvailabilityDialogOpen(false)}>{tm('Close')}</button>
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <button className="rounded-[28px] border border-emerald-200 bg-[linear-gradient(180deg,#ecfdf5_0%,#ffffff_100%)] px-5 py-5 text-left hover:border-emerald-300 hover:bg-emerald-50" onClick={() => { setAddAvailabilityDialogOpen(false); openWorkingDialog(); }}>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600">{tm('Base schedule')}</div>
                  <div className="mt-2 text-lg font-semibold text-emerald-950">{tm('Recurring Working Hours')}</div>
                  <div className="mt-2 text-sm text-emerald-800">{tm("Staff-facing availability used as the provider's weekly foundation.")}</div>
                </button>
                <button className="rounded-[28px] border border-sky-200 bg-[linear-gradient(180deg,#eff6ff_0%,#ffffff_100%)] px-5 py-5 text-left hover:border-sky-300 hover:bg-sky-50" onClick={() => { setAddAvailabilityDialogOpen(false); openWindowDialog(); }}>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">{tm('Patient booking')}</div>
                  <div className="mt-2 text-lg font-semibold text-sky-950">{tm('Portal Bookable Window')}</div>
                  <div className="mt-2 text-sm text-sky-800">{tm('Recurring bookable slots with visit-type, buffer, and visibility rules.')}</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {workingDialog.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8 backdrop-blur-sm">
          <div className="w-full max-w-5xl rounded-[32px] bg-slate-50 p-2 shadow-2xl">
            <div className="max-h-[90vh] overflow-y-auto rounded-[28px] bg-white p-5">
              <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Weekly Planner')}</p>
                  <h4 className="text-2xl font-semibold text-ink">{workingDialog.index === null ? tm('Add working availability') : tm('Edit working availability')}</h4>
                  <p className="mt-1 text-sm text-slate-500">{tm("Use an Outlook-style weekly recurrence pattern for the provider's base working hours.")}</p>
                </div>
                <button className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={closeWorkingDialog}>{tm('Close')}</button>
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
                <aside className="rounded-[28px] border border-emerald-200 bg-[linear-gradient(180deg,#ecfdf5_0%,#ffffff_100%)] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600">{tm('Series summary')}</p>
                  <h5 className="mt-2 text-xl font-semibold text-emerald-950">{tm('Recurring working hours')}</h5>
                  <p className="mt-2 text-sm text-emerald-800">{tm('This series creates one recurring schedule row for each checked weekday.')}</p>
                  <div className="mt-4 space-y-2">
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-600">{tm('Pattern')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{recurrenceLabel(workingDialog.value.recurrenceIntervalWeeks)} {tm('on')} {weekdayPatternSummaryLocalized(workingDialog.selectedWeekdays)}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-600">{tm('Time')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{rangeLabel(workingDialog.value.startMinute, workingDialog.value.endMinute)}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-600">{tm('Range')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{workingDialog.value.recurrenceStartDate || tm('Not set')} {tm('to')} {workingDialog.value.recurrenceEndDate || tm('Not set')}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-600">{tm('Status')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{workingDialog.value.active ? tm('Active') : tm('Inactive')}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-600">{tm('Location')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{locationLabel(workingDialog.value.locationId)}</div>
                    </div>
                  </div>
                </aside>

                <div className="space-y-4">
                  <PlannerSection title={tm('Schedule pattern')} description={tm('Define the daily work block and weekly recurrence in one place.')}>
                    <div className="space-y-3">
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px]">
                        <label className="text-sm">
                          <span className="mb-1 block text-slate-500">{tm('Start')}</span>
                          <input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={minuteToInput(workingDialog.value.startMinute)} onChange={(event) => setWorkingDialog((current) => ({ ...current, value: { ...current.value, startMinute: inputToMinute(event.target.value) } }))} />
                        </label>
                        <label className="text-sm">
                          <span className="mb-1 block text-slate-500">{tm('End')}</span>
                          <input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={minuteToInput(workingDialog.value.endMinute)} onChange={(event) => setWorkingDialog((current) => ({ ...current, value: { ...current.value, endMinute: inputToMinute(event.target.value) } }))} />
                        </label>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Series status')}</div>
                          <Toggle label={tm('Active')} checked={workingDialog.value.active} onChange={(checked) => setWorkingDialog((current) => ({ ...current, value: { ...current.value, active: checked } }))} />
                        </div>
                      </div>
                      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                        <label className="text-sm">
                          <span className="mb-1 block text-slate-500">{tm('Location')}</span>
                          <select
                            className="w-full rounded-lg border border-slate-200 px-3 py-2"
                            value={workingDialog.value.locationId || ''}
                            onChange={(event) => setWorkingDialog((current) => ({ ...current, value: { ...current.value, locationId: event.target.value } }))}
                          >
                            <option value="">{tm('Select location')}</option>
                            {locationOptions.map((location) => (
                              <option key={location.id} value={location.id}>
                                {location.name}{location.isPrimary ? ` (${tm('Primary')})` : ''}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="border-t border-slate-100 pt-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Recurrence')}</div>
                        <WeekdaySelector selectedWeekdays={workingDialog.selectedWeekdays} onChange={updateWorkingSelectedWeekdays} tr={tm} />
                        <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                          <label className="text-sm">
                            <span className="mb-1 block text-slate-500">{tm('Repeat every')}</span>
                            <div className="flex items-center gap-2">
                              <input type="number" min={1} className="w-full rounded-lg border border-slate-200 px-3 py-2" value={workingDialog.value.recurrenceIntervalWeeks} onChange={(event) => setWorkingDialog((current) => ({ ...current, value: { ...current.value, recurrenceIntervalWeeks: Number(event.target.value) || 1 } }))} />
                              <span className="text-sm text-slate-500">{tm('week(s)')}</span>
                            </div>
                          </label>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Preview')}</div>
                            <div className="mt-1 font-medium text-ink">{recurrenceLabel(workingDialog.value.recurrenceIntervalWeeks)} {tm('on')} {weekdayPatternSummaryLocalized(workingDialog.selectedWeekdays)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </PlannerSection>

                  <PlannerSection title={tm('Range of recurrence')} description={tm('Set how long this series should remain active in the provider schedule.')}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="text-sm">
                        <span className="mb-1 block text-slate-500">{tm('Start date')}</span>
                        <input type="date" className="w-full rounded-xl border border-slate-200 px-3 py-2.5" value={workingDialog.value.recurrenceStartDate} onChange={(event) => setWorkingDialog((current) => ({ ...current, value: { ...current.value, recurrenceStartDate: event.target.value } }))} />
                      </label>
                      <label className="text-sm">
                        <span className="mb-1 block text-slate-500">{tm('End date')}</span>
                        <input type="date" className="w-full rounded-xl border border-slate-200 px-3 py-2.5" value={workingDialog.value.recurrenceEndDate} onChange={(event) => setWorkingDialog((current) => ({ ...current, value: { ...current.value, recurrenceEndDate: event.target.value } }))} />
                      </label>
                    </div>
                  </PlannerSection>
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={closeWorkingDialog}>{tm('Cancel')}</button>
                <button className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={saveWorkingDialog} disabled={!workingDialog.selectedWeekdays.length}>{tm('Save availability')}</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {windowDialog.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8 backdrop-blur-sm">
          <div className="w-full max-w-6xl rounded-[32px] bg-slate-50 p-2 shadow-2xl">
            <div className="max-h-[90vh] overflow-y-auto rounded-[28px] bg-white p-5">
              <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Weekly Planner')}</p>
                  <h4 className="text-2xl font-semibold text-ink">{windowDialog.index === null ? tm('Add portal availability') : tm('Edit portal availability')}</h4>
                  <p className="mt-1 text-sm text-slate-500">{tm('Set a recurring patient-bookable series with the same weekly recurrence style used in Outlook meetings.')}</p>
                </div>
                <button className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={closeWindowDialog}>{tm('Close')}</button>
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
                <aside className="rounded-[28px] border border-sky-200 bg-[linear-gradient(180deg,#eff6ff_0%,#ffffff_100%)] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">{tm('Series summary')}</p>
                  <h5 className="mt-2 text-xl font-semibold text-sky-950">{tm('Portal bookable window')}</h5>
                  <p className="mt-2 text-sm text-sky-800">{tm('Each checked weekday becomes its own recurring portal row, with one shared booking rule set.')}</p>
                  <div className="mt-4 space-y-2">
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-600">{tm('Visit type')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{typeName(windowDialog.value.appointmentTypeId)}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-600">{tm('Pattern')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{recurrenceLabel(windowDialog.value.recurrenceIntervalWeeks)} {tm('on')} {weekdayPatternSummaryLocalized(windowDialog.selectedWeekdays)}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-600">{tm('Time')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{rangeLabel(windowDialog.value.startMinute, windowDialog.value.endMinute)}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-600">{tm('Slots')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{windowDialog.value.slotDurationMinutes} {tm('min')} · {tm('Max')} {windowDialog.value.maxBookingsPerSlot}</div>
                    </div>
                    <div className="rounded-2xl border border-white/80 bg-white/80 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-600">{tm('Location')}</div>
                      <div className="mt-1 text-sm font-semibold text-ink">{locationLabel(windowDialog.value.locationId)}</div>
                    </div>
                  </div>
                </aside>

                <div className="space-y-4">
                  <PlannerSection title={tm('Schedule pattern')} description={tm('Choose the visit setup and weekly recurrence in one combined section.')}>
                    <div className="space-y-3">
                      <div>
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Appointment time')}</div>
                        <div className="grid gap-3 md:grid-cols-4">
                          <label className="text-sm">
                            <span className="mb-1 block text-slate-500">{tm('Visit type')}</span>
                            <select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.appointmentTypeId} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, appointmentTypeId: event.target.value } }))}>
                              <option value="">{tm('Visit type')}</option>
                              {appointmentTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                            </select>
                          </label>
                          <label className="text-sm">
                            <span className="mb-1 block text-slate-500">{tm('Location')}</span>
                            <select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.locationId || ''} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, locationId: event.target.value } }))}>
                              <option value="">{tm('Select location')}</option>
                              {locationOptions.map((location) => (
                                <option key={location.id} value={location.id}>
                                  {location.name}{location.isPrimary ? ` (${tm('Primary')})` : ''}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="text-sm">
                            <span className="mb-1 block text-slate-500">{tm('Start')}</span>
                            <input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={minuteToInput(windowDialog.value.startMinute)} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, startMinute: inputToMinute(event.target.value) } }))} />
                          </label>
                          <label className="text-sm">
                            <span className="mb-1 block text-slate-500">{tm('End')}</span>
                            <input type="time" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={minuteToInput(windowDialog.value.endMinute)} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, endMinute: inputToMinute(event.target.value) } }))} />
                          </label>
                        </div>
                      </div>
                      <div className="border-t border-slate-100 pt-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Recurrence')}</div>
                        <WeekdaySelector selectedWeekdays={windowDialog.selectedWeekdays} onChange={updateWindowSelectedWeekdays} tr={tm} />
                        <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                          <label className="text-sm">
                            <span className="mb-1 block text-slate-500">{tm('Repeat every')}</span>
                            <div className="flex items-center gap-2">
                              <input type="number" min={1} className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.recurrenceIntervalWeeks} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, recurrenceIntervalWeeks: Number(event.target.value) || 1 } }))} />
                              <span className="text-sm text-slate-500">{tm('week(s)')}</span>
                            </div>
                          </label>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Preview')}</div>
                            <div className="mt-1 font-medium text-ink">{recurrenceLabel(windowDialog.value.recurrenceIntervalWeeks)} {tm('on')} {weekdayPatternSummaryLocalized(windowDialog.selectedWeekdays)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </PlannerSection>

                  <PlannerSection title={tm('Booking rules and range')} description={tm('Control slot behavior, visibility, and how long this recurring series stays active.')}>
                    <div className="space-y-3">
                      <div>
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Slot settings')}</div>
                        <div className="grid gap-3 md:grid-cols-4">
                          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Slot duration')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.slotDurationMinutes} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, slotDurationMinutes: Number(event.target.value) || 30 } }))} /></label>
                          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Buffer before')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.bufferBeforeMinutes} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, bufferBeforeMinutes: Number(event.target.value) || 0 } }))} /></label>
                          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Buffer after')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.bufferAfterMinutes} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, bufferAfterMinutes: Number(event.target.value) || 0 } }))} /></label>
                          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Max bookings')}</span><input type="number" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.maxBookingsPerSlot} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, maxBookingsPerSlot: Number(event.target.value) || 1 } }))} /></label>
                        </div>
                      </div>
                      <div className="border-t border-slate-100 pt-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Visibility and access')}</div>
                        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"><Toggle label={tm('Portal visible')} checked={windowDialog.value.visibleInPortal} onChange={(checked) => setWindowDialog((current) => ({ ...current, value: { ...current.value, visibleInPortal: checked } }))} /></div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"><Toggle label={tm('Active')} checked={windowDialog.value.active} onChange={(checked) => setWindowDialog((current) => ({ ...current, value: { ...current.value, active: checked } }))} /></div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"><Toggle label={tm('Existing patients only')} checked={windowDialog.value.existingPatientsOnly} onChange={(checked) => setWindowDialog((current) => ({ ...current, value: { ...current.value, existingPatientsOnly: checked } }))} /></div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"><Toggle label={tm('Allow new patients')} checked={windowDialog.value.newPatientsAllowed} onChange={(checked) => setWindowDialog((current) => ({ ...current, value: { ...current.value, newPatientsAllowed: checked } }))} /></div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 xl:col-span-2"><Toggle label={tm('Allow outside working hours')} checked={Boolean(windowDialog.value.allowOutsideWorkingHours)} onChange={(checked) => setWindowDialog((current) => ({ ...current, value: { ...current.value, allowOutsideWorkingHours: checked } }))} /></div>
                        </div>
                      </div>
                      <div className="border-t border-slate-100 pt-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tm('Range of recurrence')}</div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('Start date')}</span><input type="date" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.recurrenceStartDate} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, recurrenceStartDate: event.target.value } }))} /></label>
                          <label className="text-sm"><span className="mb-1 block text-slate-500">{tm('End date')}</span><input type="date" className="w-full rounded-lg border border-slate-200 px-3 py-2" value={windowDialog.value.recurrenceEndDate} onChange={(event) => setWindowDialog((current) => ({ ...current, value: { ...current.value, recurrenceEndDate: event.target.value } }))} /></label>
                        </div>
                      </div>
                    </div>
                  </PlannerSection>
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={closeWindowDialog}>{tm('Cancel')}</button>
                <button className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" onClick={saveWindowDialog} disabled={!windowDialog.selectedWeekdays.length}>{tm('Save availability')}</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
